import { motion } from 'framer-motion';
import * as React from 'react';

import AnswerGrid from './AnswerGrid';
import CategoryPicker from './CategoryPicker';
import Message from './Message';
import ScoreSummary from './ScoreSummary';
import Scoreboard from './Scoreboard';
import SearchBox from './SearchBox';
import SearchButtons from './SearchButtons';

import { Categories, LoadQuery, QOTD, Round } from '.';
import '../../styles/Game.css';

const GameLayout = ({
	answers,
	categories,
	category,
	correctAnswers,
	giveUpFunction,
	highScore,
	loadingCategory,
	loadQuery,
	modular,
	nextRound,
	numberOfGuessesOrStatic,
	numberOfRoundsOrQOTD,
	processedAnswers,
	query,
	questionOfTheDay,
	response,
	round,
	score,
	setLoadingCategory,
	setResponse,
	useStaticQuery,
	wrong,
	giveUp,
	shareText,
	submitAnswer,
	showResults,
	setShowResults,
	roundSummary,
}: {
	answers?: { [key: string]: string[] };
	categories: Categories;
	category: string;
	correctAnswers: boolean[];
	giveUpFunction: () => void;
	highScore: number;
	loadingCategory: string;
	loadQuery: (arg0: LoadQuery) => void;
	modular?: boolean;
	nextRound: (arg0: string) => void;
	numberOfGuessesOrStatic: number;
	numberOfRoundsOrQOTD: number;
	processedAnswers: string[];
	query: string;
	questionOfTheDay?: QOTD;
	response: string;
	round: number;
	score: number;
	setLoadingCategory: (key: string) => void;
	setResponse: (key: string) => void;
	useStaticQuery: string;
	wrong: number;
	giveUp: boolean;
	shareText: () => string;
	submitAnswer: () => void;
	showResults: boolean;
	setShowResults: (key: boolean) => void;
	roundSummary: Round[];
}) => (
	<div id="GameWrapper" style={{ textAlign: 'center' }}>
		{!modular && (
			<Message
				averageScore={questionOfTheDay?.averageScore ?? 0}
				category={category}
				categorySelect={!query}
				endOfGame={
					round === numberOfRoundsOrQOTD && wrong === numberOfGuessesOrStatic
				}
				endOfRound={wrong === numberOfGuessesOrStatic}
				gotAllTheAnswers={
					correctAnswers.filter((val) => val).length === processedAnswers.length
				}
				loading={!answers && !!loadingCategory}
				newHighScore={score > highScore}
				qotd={category === 'Question of the Day'}
				round={round}
				score={score}
				showResults={showResults}
				useStaticQuery={useStaticQuery}
			/>
		)}
		<div id="SearchBoxWrapper">
			<motion.div
				key="sb"
				animate={{
					borderColor: !query ? 'rgba(0,0,0,0)' : 'var(--gray)',
					transition: { duration: 0.5 },
				}}
				id="SearchBox"
				layout
				style={{
					border: '1px solid rgba(0,0,0,0)',
					borderRadius: 'var(--single)',
					display: 'inline-block',
					minWidth: '600px',
					padding: '0',
				}}
				transition={{
					bounce: 0.5,
					duration: 1,
					type: 'spring',
				}}>
				{!query ? (
					<CategoryPicker
						key="cp"
						categories={categories}
						disabled={!categories}
						loadingCategory={!answers ? loadingCategory : ''}
						loadQuery={loadQuery}
						questionOfTheDay={questionOfTheDay}
						setLoadingCategory={setLoadingCategory}
					/>
				) : (
					<>
						{showResults ? (
							<ScoreSummary roundSummary={roundSummary} score={score} />
						) : (
							<>
								<SearchBox
									clear={() => setResponse('')}
									giveUp={giveUp}
									numberOfGuessesOrStatic={numberOfGuessesOrStatic}
									query={query}
									response={response}
									setResponse={setResponse}
									submitAnswer={submitAnswer}
									useStaticQuery={useStaticQuery}
									wrong={wrong}
								/>
								<hr />
								<AnswerGrid
									answers={answers?.[query]}
									correctAnswers={correctAnswers}
									easyMode={category === 'Easy'}
									giveUp={giveUp}
									query={query}
									roundOver={wrong === numberOfGuessesOrStatic}
									useStaticQuery={useStaticQuery}
								/>
							</>
						)}
						<SearchButtons
							category={category}
							disabled={wrong === numberOfGuessesOrStatic}
							gameOver={round === numberOfRoundsOrQOTD}
							giveUp={giveUpFunction}
							nextRound={nextRound}
							roundOver={wrong === numberOfGuessesOrStatic}
							setShowResults={setShowResults}
							shareText={shareText}
							showResults={showResults}
							submitAnswer={submitAnswer}
							useStaticQuery={useStaticQuery}
						/>
					</>
				)}
			</motion.div>
		</div>
		{!modular && (
			<Scoreboard
				highScore={highScore}
				isQOTD={category === 'Question of the Day'}
				round={round}
				score={score}
				useStaticQuery={useStaticQuery}
				wrong={wrong}
			/>
		)}
	</div>
);

export default GameLayout;
